import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { generatePhrase, slugify, cleanRoomId, getExamplePhrases } from '../utils/phrase';
import { showPopup, showToast } from '../utils/notifications';
import { getFeaturedBlogs } from '../data/blogData';
import AdSenseUnit from '../components/AdSenseUnit';
import ScrollToTop from '../components/ScrollToTop';
import { Popover } from 'bootstrap';
import '../styles/HomePage.css';

export default function HomePage() {
  const navigate = useNavigate();
  const [roomId, setRoomId] = useState('');
  const [placeholderText, setPlaceholderText] = useState('');
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const placeholderIndex = useRef(0);
  const typingIndex = useRef(0);
  const isDeleting = useRef(false);
  const featuredBlogs = getFeaturedBlogs();

  // Scroll progress bar
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    setScrollProgress(progress);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);
  
  // Animated placeholder effect
  useEffect(() => {
    const examples = getExamplePhrases();
    let timeout;

    const animatePlaceholder = () => {
      const currentExample = examples[placeholderIndex.current];
      
      if (!isDeleting.current) {
        // Typing
        if (typingIndex.current < currentExample.length) {
          setPlaceholderText(currentExample.substring(0, typingIndex.current + 1));
          typingIndex.current++;
          timeout = setTimeout(animatePlaceholder, 100);
        } else {
          // Pause before deleting
          timeout = setTimeout(() => {
            isDeleting.current = true;
            animatePlaceholder();
          }, 2000);
        }
      } else {
        // Deleting
        if (typingIndex.current > 0) {
          setPlaceholderText(currentExample.substring(0, typingIndex.current - 1));
          typingIndex.current--;
          timeout = setTimeout(animatePlaceholder, 50);
        } else {
          // Move to next example
          isDeleting.current = false;
          placeholderIndex.current = (placeholderIndex.current + 1) % examples.length;
          timeout = setTimeout(animatePlaceholder, 500);
        }
      }
    };

    animatePlaceholder();
    return () => clearTimeout(timeout);
  }, []);

  // PWA Installation
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPWAPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);


  // Ad blocker detection
  useEffect(() => {
    const detectAdBlocker = async () => {
      try {
        await fetch(
          'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js',
          { method: 'HEAD', mode: 'no-cors' }
        );
        // If we get here, ad blocker is not active
      } catch (error) {
        // Ad blocker detected
        showPopup({
          title: 'Ad Blocker Detected',
          description: 'We noticed you are using an ad blocker. MeetingPoint is free and ad-supported. Please consider disabling your ad blocker to support us.',
          type: 'warning'
        });
      }
    };

    const timer = setTimeout(detectAdBlocker, 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleInstallPWA = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      showToast('App installed successfully!', 'success');
    }

    setDeferredPrompt(null);
    setShowPWAPrompt(false);
  };
  
  const createRoom = async () => {
    const phrase = generatePhrase();
    const newRoomId = slugify(phrase);

    // Send notification BEFORE redirecting (with 5s timeout fallback)
    const apiBase = process.env.REACT_APP_API_BASE || '';
    try {
      await Promise.race([
        fetch(`${apiBase}/api/notify-room-created`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ roomId: newRoomId }),
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000)),
      ]);
    } catch (e) {
      // Don't block the user if notification fails
      console.warn('[MeetingPoint] Notification failed:', e.message);
    }

    navigate(`/chat/${newRoomId}`);
  };

  const joinRoom = () => {
    if (!roomId.trim()) {
      showToast('Please enter a room link or phrase to join a chat', 'warning');
      return;
    }
    const cleanId = cleanRoomId(roomId);
    navigate(`/chat/${cleanId}`);
  };

  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      joinRoom();
    }
  };

  // Initialize Bootstrap popovers
  useEffect(() => {
    const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
    const popoverList = [...popoverTriggerList].map(el => {
      return new Popover(el, {
        trigger: 'hover focus',
        html: true
      });
    });

    return () => {
      popoverList.forEach(popover => popover.dispose());
    };
  }, []);

  return (
    <div
      className="home-container"
      style={{
        backgroundImage: "url('/images/background.png')",
        backgroundPosition: 'center',
        backgroundSize: 'cover'
      }}
    >
      {/* Scroll Progress Bar */}
      <div className="scroll-progress-track">
        <div
          className="scroll-progress-bar"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Navbar */}
      <nav className="navbar navbar-expand-lg">
        <div className="container">
          <a className="navbar-brand" href="/">
            <img src="/images/sidebar-logo.png" alt="MeetingPoint Logo" loading="lazy" />
          </a>
          <div className="collapse navbar-collapse justify-content-center" id="navbarNav">
            <ul className="navbar-nav">
              <li className="nav-item">
                <a className="nav-link active" aria-current="page" href="/">Home</a>
              </li>
              <li className="nav-item">
                <Link to="/about" className="nav-link text-white">About Us</Link>
              </li>
              <li className="nav-item">
                <button type="button" className="nav-link btn btn-link text-white" onClick={() => setShowHowItWorksModal(true)}>How It Works</button>
              </li>
              <li className="nav-item">
                <Link to="/contact" className="nav-link text-white">Contact Us</Link>
              </li>
              <li className="nav-item">
                <Link to="/blogs" className="nav-link text-white">Blog</Link>
              </li>
              <li className="nav-item">
                <a href="https://en.wikipedia.org/wiki/Peer-to-peer" target="_blank" rel="noreferrer" className="nav-link text-white">Peer To Peer</a>
              </li>
            </ul>
          </div>
          
          <button 
            className="btn btn-signup" 
            type="button" 
            data-bs-toggle="popover" 
            data-bs-placement="bottom" 
            data-bs-content="Don't worry about it. No sign-up is required. Keep going...">
            No Sign Up
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="text-center mt-3">
        <span className="hero-chip">MeetingPoint</span>
      </div>
      <div className="text-center mt-2">
        <h2 className="header-text">Instant <span className="gradient-text">Connections.</span></h2>
      </div>
      <div className="text-center">
        <h2 className="header-text">Private <span className="gradient-text">Conversations.</span></h2>
      </div>
      <div className="text-center mt-2">
        <p className="text-dark" style={{ maxWidth: '600px', margin: '10px auto', opacity: 0.85, fontSize: '1.05rem' }}>
          No accounts. No downloads. No signin, just a secure, encrypted link between you and whoever you're talking with.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-center align-items-center mt-4">
        <div className="d-block d-sm-flex align-items-center chat-link-wrapper gap-3">
          <button 
            className="btn btn-gradient btn-start-chat w-100 px-4 d-flex align-items-center justify-content-center gap-2" 
            style={{ minWidth: '150px' }} 
            onClick={createRoom}
          >
            Start a New Chat
          </button>
          <div className="fw-bold my-2 text-center">OR</div>
          <div className="input-group url-text-parent" style={{ minWidth: '320px', maxWidth: '320px' }}>
            <input 
              id="roomId"
              type="text" 
              className="form-control url-text" 
              placeholder={placeholderText}
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              onKeyPress={handleInputKeyPress}
            />
            <button 
              className="btn btn-gradient" 
              onClick={joinRoom}
            >
              Join a Chat
            </button>
          </div>
        </div>
      </div>

      {/* App Screenshot */}
      <div className="text-center mt-5 row">
        <div className="col-md-5 ads-block d-none d-md-flex justify-content-center align-items-center">
          <AdSenseUnit adSlot="7252016083" adFormat="auto" />
        </div>
        <div className="col-md-2">
          <img 
            loading="lazy" 
            className="w-100 main-phone-img" 
            src="/images/phone.png" 
            alt="MeetingPoint App Screenshot" 
          />
        </div>
        <div className="col-md-5 ads-block d-none d-md-flex justify-content-center align-items-center">
          <AdSenseUnit adSlot="4625852742" adFormat="auto" />
        </div>
      </div>

      {/* Section: Why We Built MeetingPoint */}
      <section className="home-section section-dark">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <span className="section-chip">OUR MISSION</span>
              <h2 className="section-title">Why We Built <span className="gradient-text">MeetingPoint</span></h2>
              <p className="section-text">
                We launched MeetingPoint to solve a specific problem in the modern digital landscape: <strong>the erosion of privacy in casual communication</strong>. Unlike major social platforms that harvest metadata, our architecture is strictly peer-to-peer (P2P).
              </p>
              <p className="section-text">
                Your data streams directly between devices—serverless, encrypted, and ephemeral. We do not store logs, we do not require emails, and we do not track your digital footprint.
              </p>
              <Link to="/blogs/why-we-built-meetingpoint" className="section-link">
                Read the full story <i className="fas fa-arrow-right ms-2"></i>
              </Link>
            </div>
            <div className="col-lg-6">
              <div className="section-features-grid">
                <div className="section-feature-card">
                  <i className="fas fa-shield-alt"></i>
                  <h5>No Data Collection</h5>
                  <p>We couldn't record your call even if we wanted to—we aren't in the loop.</p>
                </div>
                <div className="section-feature-card">
                  <i className="fas fa-user-secret"></i>
                  <h5>No Sign-Up Required</h5>
                  <p>No email, no phone number, no account. Just click and communicate.</p>
                </div>
                <div className="section-feature-card">
                  <i className="fas fa-lock"></i>
                  <h5>End-to-End Encrypted</h5>
                  <p>WebRTC mandates DTLS-SRTP encryption on all media streams.</p>
                </div>
                <div className="section-feature-card">
                  <i className="fas fa-trash-alt"></i>
                  <h5>Ephemeral by Design</h5>
                  <p>Close the tab and the connection is severed. Nothing remains.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Real-World Applications */}
      <section className="home-section section-light">
        <div className="container">
          <div className="text-center mb-5">
            <span className="section-chip">USE CASES</span>
            <h2 className="section-title">Real-World <span className="gradient-text">Applications</span></h2>
            <p className="section-subtitle">From freelancers to gamers, from journalists to therapists—MeetingPoint serves anyone who values private, instant communication.</p>
          </div>
          <div className="use-case-grid">
            <div className="use-case-card">
              <div className="use-case-icon"><i className="fas fa-plane-departure"></i></div>
              <h4>The Altitude Advantage</h4>
              <p>Stuck at 35,000 feet? While the airline firewall blocks your "big name" apps, MeetingPoint slips through. If you can load this page, you can have a private conversation.</p>
            </div>
            <div className="use-case-card">
              <div className="use-case-icon"><i className="fas fa-briefcase"></i></div>
              <h4>Freelancer's Sanctuary</h4>
              <p>Hiring a pro on Upwork or Fiverr? Keep your personal WhatsApp and phone number off the table. Use us as your digital neutral zone.</p>
            </div>
            <div className="use-case-card">
              <div className="use-case-icon"><i className="fas fa-shopping-cart"></i></div>
              <h4>Marketplace Trust</h4>
              <p>Verify eBay and Reverb items live before you buy. See the real condition, hear the actual sound — no phone numbers exchanged.</p>
            </div>
            <div className="use-case-card">
              <div className="use-case-icon"><i className="fas fa-shield-alt"></i></div>
              <h4>Privacy by Physics</h4>
              <p>We don't "promise" not to look at your data. We built a system where it's physically impossible for us to see it. Period.</p>
            </div>
            <div className="use-case-card">
              <div className="use-case-icon"><i className="fas fa-gamepad"></i></div>
              <h4>Gamer's Lobby</h4>
              <p>Instant voice coordination — drop a link, squad up, play. No installs, zero FPS impact.</p>
            </div>
            <div className="use-case-card">
              <div className="use-case-icon"><i className="fas fa-user-secret"></i></div>
              <h4>Source Protection</h4>
              <p>Journalists and legal professionals get off-the-record channels with no metadata trail. The meeting vanishes when you close the tab.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section: How P2P Protects You */}
      <section className="home-section section-dark">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-5 mb-4 mb-lg-0 text-center">
              <div className="p2p-diagram">
                <div className="p2p-node p2p-you">
                  <i className="fas fa-user"></i>
                  <span>You</span>
                </div>
                <div className="p2p-line">
                  <span className="p2p-label">Direct P2P</span>
                </div>
                <div className="p2p-node p2p-peer">
                  <i className="fas fa-user-friends"></i>
                  <span>Peer</span>
                </div>
              </div>
            </div>
            <div className="col-lg-7">
              <span className="section-chip">TECHNOLOGY</span>
              <h2 className="section-title">How P2P <span className="gradient-text">Protects You</span></h2>
              <p className="section-text">
                Most chat apps use a <strong>"Client → Server → Client"</strong> model—the server owner has a copy of your conversation. MeetingPoint uses WebRTC: your browser performs a handshake with your partner's browser, and once connected, all data flows <strong>directly IP-to-IP</strong>.
              </p>
              <p className="section-text">
                We couldn't record your call even if we wanted to—we simply aren't in the loop. It's like introducing two people at a party and then walking away.
              </p>
              <Link to="/blogs/how-peer-to-peer-protects-you" className="section-link">
                Deep dive into P2P security <i className="fas fa-arrow-right ms-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* AdSense Unit - Before Blog Section */}
      <div className="container">
        <AdSenseUnit adSlot="7415667611" adFormat="auto" />
      </div>

      {/* Section: Featured Blogs */}
      <section className="home-section section-light">
        <div className="container">
          <div className="text-center mb-5">
            <span className="section-chip">INSIGHTS</span>
            <h2 className="section-title">From Our <span className="gradient-text">Blog</span></h2>
            <p className="section-subtitle">Explore how peer-to-peer communication is reshaping privacy, collaboration, and the way we connect.</p>
          </div>
          <div className="home-blogs-grid">
            {featuredBlogs.slice(0, 3).map(post => (
              <Link to={`/blogs/${post.slug}`} key={post.slug} className="home-blog-card">
                <div className="home-blog-icon">
                  <i className={`fas ${post.icon}`}></i>
                </div>
                <span className="home-blog-category">{post.category}</span>
                <h4 className="home-blog-title">{post.title}</h4>
                <p className="home-blog-excerpt">{post.excerpt}</p>
                <span className="home-blog-read">Read article <i className="fas fa-arrow-right ms-1"></i></span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/blogs" className="btn btn-gradient">
              View All Articles <i className="fas fa-arrow-right ms-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer px-2 py-5">
        <div className="row">
          <div className="col-8 offset-2">
            <div className="row mb-5">
              <div className="col-12">
                <h4 className="text-white mb-3">Why Peer to Peer?</h4>
                <div className="p2-content-wrapper">
                  <p className="text-light p2-text">
                    Peer-to-peer (P2P) connections have become a fundamental aspect of modern communication technologies, particularly in applications that require real-time data exchange, such as video conferencing, file sharing, and online gaming. The primary advantage of P2P connections lies in their ability to facilitate direct communication between users without the need for a centralized server. This decentralized approach not only enhances the efficiency of data transfer but also reduces latency, which is crucial for applications that demand real-time interaction. By allowing devices to connect directly, P2P technology minimizes the delays associated with routing data through a central server, resulting in a smoother and more responsive user experience.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="row align-items-start text-center text-md-start">
              <div className="col-md-4 mb-4 mb-md-0">
                <h2 className="footer-logo mb-2">MeetingPoint..<span className="logo-icon">➤</span></h2>
                <p className="footer-tagline">Instant <span className="highlight-text">Connections.</span><br />Private <span className="highlight-text">Conversations.</span></p>
              </div>

              <div className="col-md-4 quick-links text-center mb-4 mb-md-0">
                <h5 className="mb-4">Quick links</h5>
                <div className="d-flex justify-content-center gap-3 link-group flex-wrap">
                  <Link to="/about" className="text-light text-decoration-none">About</Link>
                  <span className="text-muted">|</span>
                  <Link to="/blogs" className="text-light text-decoration-none">Blog</Link>
                  <span className="text-muted">|</span>
                  <Link to="/faq" className="text-light text-decoration-none">FAQ</Link>
                  <span className="text-muted">|</span>
                  <Link to="/contact" className="text-light text-decoration-none">Contact</Link>
                  <span className="text-muted">|</span>
                  <Link to="/privacy" className="text-light text-decoration-none">Privacy</Link>
                  <span className="text-muted">|</span>
                  <Link to="/terms" className="text-light text-decoration-none">Terms</Link>
                  <span className="text-muted">|</span>
                  <Link to="/disclaimer" className="text-light text-decoration-none">Disclaimer</Link>
                </div>
              </div>

              <div className="col-md-4 feedback-section text-center text-md-end">
                <h5 className="mb-4">We want your feedback</h5>
                <button className="submit-btn" onClick={() => setShowFeedbackModal(true)}>
                  Feedback <span className="ms-2">➔</span>
                </button>
              </div>
            </div>

            <div className="footer-bottom mt-5 pt-4 d-flex flex-column flex-md-row justify-content-between align-items-center">
              <span className="text-muted mb-3 mb-md-0">Copyright © {new Date().getFullYear()} MeetingPoint. All rights reserved</span>
              <div className="social-icons">
                <button 
                  className="social-btn" 
                  data-bs-toggle="popover" 
                  data-bs-placement="top" 
                  data-bs-content="Just kidding. We're not on Facebook. We value our privacy too much.">
                  <i className="fab fa-facebook-f"></i>
                </button>
                <button 
                  className="social-btn" 
                  data-bs-toggle="popover" 
                  data-bs-placement="top" 
                  data-bs-content="We prefer the anonymity of our platform to the public scrutiny of Twitter.">
                  <i className="fab fa-twitter"></i>
                </button>
                <button 
                  className="social-btn" 
                  data-bs-toggle="popover" 
                  data-bs-placement="top" 
                  data-bs-content="Forget the job hunt, let's just chat.">
                  <i className="fab fa-linkedin-in"></i>
                </button>
                <button 
                  className="social-btn" 
                  data-bs-toggle="popover" 
                  data-bs-placement="top" 
                  data-bs-content="We don't want to compete with cats and makeup tutorials.">
                  <i className="fab fa-youtube"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* PWA Install Prompt */}
      {showPWAPrompt && (
        <div className="pwa-prompt">
          <div className="pwa-content">
            <h4>Install MeetingPoint</h4>
            <p>Install our app for a better experience!</p>
            <div className="pwa-buttons">
              <button className="btn btn-primary" onClick={handleInstallPWA}>Install</button>
              <button className="btn btn-secondary" onClick={() => setShowPWAPrompt(false)}>Not now</button>
            </div>
          </div>
        </div>
      )}

      {/* How It Works Modal */}
      {showHowItWorksModal && (
        <div className="modal-overlay" onClick={() => setShowHowItWorksModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowHowItWorksModal(false)}><i className="fas fa-times"></i></button>
            <h2 className="hiw-modal-title">How It <span className="gradient-text">Works</span></h2>
            <div className="how-it-works-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>Create or <span className="gradient-text">Join</span> a Room</h4>
                  <p>Click "Start a new Chat" to create a room with a unique phrase, or enter an existing room ID to join.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>Share the <span className="gradient-text">Link</span></h4>
                  <p>Copy and share the room link with the people you want to chat with.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>Start <span className="gradient-text">Chatting</span></h4>
                  <p>Once connected, you can send messages, share files, and start video/audio calls - all peer-to-peer!</p>
                </div>
              </div>
            </div>
            <div className="hiw-features">
              <h4>Key <span className="gradient-text">Features</span></h4>
              <ul>
                <li>Text messaging with emoji support</li>
                <li>File sharing (images, videos, documents)</li>
                <li>Video and audio calls</li>
                <li>Screen sharing</li>
                <li>Up to 4 participants per room</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="modal-overlay" onClick={() => setShowFeedbackModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowFeedbackModal(false)}><i className="fas fa-times"></i></button>
            <h2 className="hiw-modal-title">Send <span className="gradient-text">Feedback</span></h2>
            <p className="feedback-subtitle">We'd love to hear from you! Share your thoughts and help us improve.</p>
            <form onSubmit={(e) => { e.preventDefault(); showToast('Thank you for your feedback!', 'success'); setShowFeedbackModal(false); }}>
              <div className="feedback-field">
                <label className="feedback-label">Your Email <span className="feedback-optional">(optional)</span></label>
                <input type="email" className="feedback-input" placeholder="your@email.com" />
              </div>
              <div className="feedback-field">
                <label className="feedback-label">Feedback Type</label>
                <select className="feedback-input">
                  <option>Bug Report</option>
                  <option>Feature Request</option>
                  <option>General Feedback</option>
                </select>
              </div>
              <div className="feedback-field">
                <label className="feedback-label">Message</label>
                <textarea className="feedback-input feedback-textarea" rows="4" placeholder="Tell us what you think..."></textarea>
              </div>
              <button type="submit" className="btn btn-gradient feedback-submit">Submit Feedback <i className="fas fa-paper-plane ms-2"></i></button>
            </form>
          </div>
        </div>
      )}

      <ScrollToTop />
    </div>
  );
}